import { useEffect, useRef } from 'react'

import { Box } from '@fortum/elemental-ui'

export type AriaLiveRegionProps = {
  children: React.ReactNode
  visuallyHidden?: boolean
  focusOn?: boolean
  politeness?: 'assertive' | 'polite'
}
/**
 * A component to show a live region for screen readers
 * @param children The content to be rendered inside the live region.
 * @param visuallyHidden If true, the live region will be visually hidden but still accessible to screen readers.
 * @param focusOn If true, the live region will receive focus when updated.
 * @param politeness - 'assertive' for critical updates that should interrupt (default), 'polite' for less urgent updates
 */
export const AriaLiveRegion = ({
  children,
  visuallyHidden = true,
  focusOn = false,
  politeness = 'assertive',
}: AriaLiveRegionProps) => {
  const regionRef = useRef<HTMLDivElement>(null)
  const didFocus = useRef(false)

  useEffect(() => {
    if (focusOn && regionRef.current && !didFocus.current) {
      regionRef.current?.focus({ preventScroll: true })
      didFocus.current = true
    } else if (!focusOn) {
      didFocus.current = false
    }
  }, [focusOn])

  return (
    <Box
      ref={regionRef}
      role="status"
      aria-live={politeness}
      tabIndex={visuallyHidden ? -1 : 0}
      style={
        visuallyHidden
          ? {
              // preventScroll works quite inconsistently between browsers
              // these styles prevent the browser from scrolling to the live region when programmatically focused
              border: 0,
              clip: 'rect(0 0 0 0)',
              height: '1px',
              position: 'absolute',
              width: '1px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }
          : undefined
      }
    >
      {children}
    </Box>
  )
}
